<template>
  <div>
    <div class="position-relative">
      <div class="workspace-title">
        <template v-if="initialized">
          <template v-if="userId === profileMe.id">My</template>
          <template v-else>{{userProfile.firstName}}'s</template>
          profile
        </template>
        <template v-else>
          Profile
        </template>
      </div>
    </div>
    <b-row>
      <b-col tag="main" lg="12" xl="9" class="mb-4">
        <div v-if="initialized">
          <user-profile-info
            :editable="isMe"
            :profile="userProfile"
            :user-kudos="computedUserKudos"
            :user-thanks="computedThanks"
            @edit="onEditProfile"
          />
          <b-modal v-if="isMe" ref="editprofile" hide-footer size="lg" title="Edit Profile">
            <edit-user-profile
              :profile="userProfile"
              :active-tab="editProfileActiveTab"
              @submitted="$refs.editprofile.hide()"
            />
          </b-modal>
          <kudos-received
            :loading="isAllKudosLoading"
            :kudos="kudos"
            :received="kudosReceived"
            class="bg-white shadow-sm p-5 mt-4"
          />
          <feedbacks-list
            :loading="isFeedbacksLoading"
            :feedbacks="computedFeedbacks"
            class="mt-4"
          />
          <professional-future
            v-if="showProfessionalFeature"
            :value="userProfile.isFutureVisible"
            :editable="isMe"
            :profile="userProfile"
            class="bg-white shadow-sm p-5 mt-4"
            @change="onProfessionalFutureChange"
            @edit="onEditProfile"
          />
        </div>
        <div v-else class="text-center p-5">
          <b-spinner variant="primary" label="Loading..." />
        </div>
      </b-col>
      <b-col lg="12" xl="3">
        <b-row>
          <b-col tag="aside" lg="6" xl="12" class="mb-4">
            <top-priorities :me="isMe" :userId="userId" class="shadow h-100" />
          </b-col>
          <b-col tag="aside" lg="6" xl="12" class="mb-4">
            <belongs-to-teams :teams="computedProfileTeams" :me="isMe" class="shadow h-100" />
          </b-col>
          <b-col tag="aside" lg="6" xl="12" class="mb-4">
            <user-location :profile="userProfile" :me="isMe" class="shadow h-100" />
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import UserProfileInfo, { ProfessionalFuture } from '@/components/UserProfile'
import EditUserProfile from './EditUserProfile.vue'
import { BelongsToTeams, UserLocation, TopPriorities } from './Aside'
import { KudosReceived } from '@/components/KudosList'
import FeedbacksList from '@/components/FeedbacksList'
import { toFormData } from '@/helpers/helpers'

export default {
  name: 'UserProfile',

  computed: {
    ...mapGetters([
      'profileMe',
      'profile',
      'teams',
      'profileTeams',
      'isKudosLoading',
      'kudos',
      'isUserKudosLoading',
      'userKudosReceived',
      'userKudos',
      'feedbacks',
      'isFeedbacksLoading',
      'userThanks',
      'isUserThanksLoading'
    ]),

    userId() {
      return Number(this.$route.params.userId)
    },

    userProfile() {
      return this.initialized ? this.profile(this.userId) : {}
    },

    isMe() {
      return this.profileMe.id === this.userId
    },

    computedProfileTeams() {
      return this.profileTeams(this.userId)
    },

    computedUserKudos() {
      return this.isUserKudosLoading ? undefined : this.userKudos(this.userId)
    },

    kudosReceived() {
      return this.isUserKudosLoading ? undefined : this.userKudosReceived(this.userId)
    },

    isAllKudosLoading() {
      return this.isUserKudosLoading || this.isKudosLoading
    },

    computedFeedbacks() {
      return this.isFeedbacksLoading ? undefined : this.feedbacks(this.userId)
    },

    computedThanks() {
      return this.isUserThanksLoading ? undefined : this.userThanks(this.userId)
    },

    showProfessionalFeature() {
      return Boolean(this.userProfile) &&
        Object.prototype.hasOwnProperty.call(this.userProfile, 'shortTermGoals') &&
        Object.prototype.hasOwnProperty.call(this.userProfile, 'midTermGoals') &&
        Object.prototype.hasOwnProperty.call(this.userProfile, 'longTermGoals')
    }
  },

  watch: {
    userId() {
      this.load()
    }
  },

  data() {
    return {
      initialized: false,
      editProfileActiveTab: 0
    }
  },

  mounted() {
    this.load()
  },

  methods: {
    load() {
      this.initialized = false
      this.$store.dispatch('fetchProfile', this.userId)
        .then(() => (this.initialized = true))

      this.$store.dispatch('fetchKudosIfNot')
      this.$store.dispatch('fetchUserKudos', this.userId)
      this.$store.dispatch('fetchUserThanks', this.userId)
      this.$store.dispatch('fetchFeedbacks', this.userId)
    },

    onEditProfile({ from }) {
      let activeTab = 0
      switch (from) {
        case 'facts':
        case 'skills':
        case 'values':
        case 'motivations':
        case 'inspiration':
        case 'professional-future':
          activeTab = 1
          break
      }

      this.editProfileActiveTab = activeTab
      this.$refs.editprofile.show()
    },

    onProfessionalFutureChange({ value }) {
      this.$store.dispatch('updateProfileMe', toFormData({ isFutureVisible: value }))
    }
  },

  components: {
    UserProfileInfo,
    EditUserProfile,
    ProfessionalFuture,
    KudosReceived,
    FeedbacksList,
    BelongsToTeams,
    UserLocation,
    TopPriorities
  }
}
</script>
